//Limitless imports
import { RESOURCE_PROCESSOR_MAP } from "@limitless/common/apis/resource-api";
import { handleSecureAjaxError, getGraphQLClient } from "../../../portal";
import { log as pilog } from "@limitless/common/utils";
import { FIND_CONTENT, DELETE_CONTENT, saveContentMutation } from "./ContentManagerQueries";
import { getCommonTransformedOptions, transformSearchOptions, processContentAPIResponse } from "./ContentManagerUtils";
const fetchContentApi = (body, processResources, shouldMergeContent) => {
  const log = pilog.getLogger("ContentManagerActions fetchContentApi");
  const updatedBody = body !== null && body !== void 0 && body.replaceFieldValue ? body : {
    ...body,
    replaceFieldValue: false
  };
  return new Promise((resolve, reject) => {
    getGraphQLClient().query({
      query: FIND_CONTENT,
      variables: updatedBody,
      fetchPolicy: body.ignoreCache ? "no-cache" : ""
    }).then(res => {
      log.debug("Content Resources retreived ", res.data.findContent);
      if (res.data.findContent.length !== 0) {
        resolve(processContentAPIResponse(res.data.findContent, processResources, shouldMergeContent));
      } else {
        resolve();
      }
    }).catch(error => {
      handleSecureAjaxError(error, "ContentManagerActions fetchContentApi", null, null, {
        2007: "2007",
        13001: "13001"
      });
      log.debug(error);
      reject();
    });
  });
};
const fetchMultipleResources = (resources, locationMap, callback) => {
  const log = pilog.getLogger("ContentManagerActions fetchMultipleResources");
  let fetchCount = 0;
  let mergedResponses = [];
  let parsedContents = [];
  const postApisResolution = () => {
    ++fetchCount;
    if (resources.length === fetchCount) {
      log.debug("all tenant contents api resolved");
      if (mergedResponses.length > 0) {
        parsedContents = mergedResponses.map(processedData => {
          processedData.content = processedData.data;
          Reflect.deleteProperty(processedData, "data");
          return {
            ...processedData,
            ...locationMap[processedData.location]
          };
        });
      }
      callback(parsedContents);
    }
  };
  resources.forEach(resource => {
    return fetchContentApi(resource, RESOURCE_PROCESSOR_MAP[resource.encoding], true).then(res => {
      if (res) {
        mergedResponses = [...mergedResponses, ...res];
      }
    }).catch(() => null).finally(() => postApisResolution());
  });
};

/**
 * Locates Content Resources for the given search options
 *
 * Callback is the result if found or undefined if not found/error
 *
 * @param {object} searchOptions
 * @param {function} callback
 */
export const findContentResources = (searchOptions, callback) => {
  const {
    body,
    shouldMergeContent,
    processResources
  } = transformSearchOptions(searchOptions);
  fetchContentApi(body, processResources, shouldMergeContent).then(res => {
    if (res) {
      callback(res.length === 1 ? res[0] : res);
    } else {
      callback();
    }
  }).catch(() => callback());
};

/**
 * Saves Content Resources in S3 via platform API
 *
 * @param {object} saveOptions
 * @param {function} callback
 */
export const saveContentResource = (saveOptions, callback) => {
  const log = pilog.getLogger("ContentManagerActions saveContentResource");
  const {
    content,
    resourceName = saveOptions.fileName
  } = saveOptions;
  getGraphQLClient().mutate({
    mutation: saveContentMutation(saveOptions),
    variables: {
      content,
      resourceName,
      ...getCommonTransformedOptions(saveOptions)
    }
  }).then(res => {
    log.debug("Content Resources Saved ", res.data);
    callback({
      success: true
    });
  }).catch(error => {
    handleSecureAjaxError(error, "ContentManagerActions saveContentResource");
    callback({
      success: false,
      error
    });
  });
};
export const deleteContentResource = (deleteOptions, callback) => {
  const log = pilog.getLogger("ContentManagerActions deleteContentResource");
  getGraphQLClient().mutate({
    mutation: DELETE_CONTENT,
    variables: {
      ...getCommonTransformedOptions(deleteOptions),
      resourceName: deleteOptions.fileName
    }
  }).then(res => {
    log.debug("Content Resources Deleted ", res.data);
    callback({
      success: true
    });
  }).catch(error => {
    handleSecureAjaxError(error, "ContentManagerActions deleteContentResource");
    callback({
      success: false,
      error
    });
  });
};
export const findTenantContentResources = (tenant, locale, resources, callback) => {
  const options = [];
  const locMap = {};
  resources.forEach(resource => {
    var _resource$application;
    locMap[`content/${locale}/tenant/${tenant.tenantName}/${resource.applicationName}`] = {
      applicationName: resource.applicationName
    };
    options.push({
      applicationName: (_resource$application = resource.applicationName) !== null && _resource$application !== void 0 ? _resource$application : "",
      folder: "",
      groupName: "",
      locale,
      tenantName: tenant.tenantName,
      encoding: resource.encoding,
      ignoreCache: true,
      resourceName: resource.name,
      baseLocation: ""
    });
  });
  fetchMultipleResources(options, locMap, callback);
};
export const findAppUpgradeContentResources = (locales, applications, callback) => {
  const options = [];
  const locMap = {};
  locales.forEach(localeItem => {
    const {
      locale
    } = localeItem;
    applications.map(app => {
      var _app$value;
      locMap[`content/${locale}/${app.value}`] = {
        applicationName: app.value,
        locale
      };
      options.push({
        applicationName: (_app$value = app.value) !== null && _app$value !== void 0 ? _app$value : "",
        folder: "",
        locale,
        encoding: "text",
        groupName: "",
        tenantName: "",
        ignoreCache: true,
        resourceName: "upgrade.json",
        baseLocation: ""
      });
    });
  });
  fetchMultipleResources(options, locMap, callback);
};
export const findMultipleContentResource = (searchOptions, callback) => {
  const options = [];
  const locMap = {};
  searchOptions.map(option => {
    const data = {
      groupName: "",
      tenantName: "",
      resourceName: "",
      encoding: "text",
      ...option
    };
    options.push(data);
  });
  fetchMultipleResources(options, locMap, callback);
};
export const saveMultipleContentResource = (resources, callback) => {
  const log = pilog.getLogger("ContentManagerActions saveMultipleContentResource");
  log.debug(resources);
  let fetchCount = 0;
  resources.forEach(resource => {
    return new Promise((resolve, reject) => {
      saveContentResource({
        content: resource.content,
        resourceName: resource.resourceName,
        ...getCommonTransformedOptions(resource)
      }, res => {
        log.debug(res);
        if (res.success) {
          resolve();
        } else {
          reject();
        }
      });
    }).then(() => ++fetchCount).catch(() => null).finally(() => {
      if (resources.length === fetchCount) {
        callback({
          hasError: false
        });
      } else {
        callback({
          hasError: true
        });
      }
    });
  });
};